import { ReactElement, ReactNode } from 'react';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import { Line, LineChart as LC } from 'recharts';
import { ChartXAxis, ChartYAxis, ChartGrid } from '@Components/Charts/chart-presets';
import { MedianOrMean } from '@blisspointmedia/bpm-types/dist/MMMUtils';
import { renderXAxisLabel } from '@Components/Charts/XAxisLabel';
import { Neutral500 } from '@src/utils/colors';
import { SaturationCentralMeasureDot } from './SaturationCentralMeasureDot';

// The x-axis (spend) and y-axis (response) diverge between the two Model Insights widgets:
// Brand Impact feeds equidistant domain/ticks + a currency x-formatter; Business Impact uses
// recharts' auto axes with an identity x-formatter + currency y-formatter. Omitted (undefined)
// fields fall back to recharts defaults, matching each caller's current markup exactly.
interface SaturationAxisConfig {
  axisLine?: boolean;
  // recharts' tickFormatter must return a string (Brand → currency; Business → String(spend)).
  tickFormatter?: (value: number) => string;
  domain?: [number, number];
  ticks?: number[];
}

interface SaturationLine {
  name: string;
  data: unknown[];
}

interface SaturationCurveChartProps {
  title: ReactNode;
  setChartRef: (ref: unknown) => void;
  xAxisHeight: number;
  yAxisWidth: number;
  xAxis: SaturationAxisConfig;
  yAxis: Omit<SaturationAxisConfig, 'axisLine'>;
  saturationData: {
    newD: SaturationLine[];
    centralMeasures: Record<string, any>;
  };
  centralMeasure: MedianOrMean;
  colorMap: Record<string, string>;
  activeLine: string | null;
  hoveredLegend: string | null;
  tooltip: ReactElement;
  // Brand Impact only shows the tooltip once a legend line is selected; Business always does.
  showTooltip: boolean;
  // X-axis label text, rendered as centered SVG text inside the axis band ("Spend" / "Positive Response").
  xAxisLabel: ReactNode;
}

// Shared saturation-curve chart body for the two Brand Equity Model Insights widgets. Owns
// the title + `.sat-chart-box` AutoSizer wrapper (which prevents the AutoSizer from measuring
// the title and overshooting — single-sourced here so the fix cannot drift between the two
// copies) + the recharts LineChart, axes, gated tooltip, and per-series Line map. The legend
// (`.right-of-chart`) stays in each parent, since it diverges (grouped vs flat).
export const SaturationCurveChart = ({
  title,
  setChartRef,
  xAxisHeight,
  yAxisWidth,
  xAxis,
  yAxis,
  saturationData,
  centralMeasure,
  colorMap,
  activeLine,
  hoveredLegend,
  tooltip,
  showTooltip,
  xAxisLabel
}: SaturationCurveChartProps): ReactElement => (
  <>
    <div className='sat-chart-title'>{title}</div>
    <div className='sat-chart-box'>
      <AutoSizer
        renderProp={({ width = 0, height = 0 }) => (
          <LC width={width} height={height} ref={setChartRef}>
            <ChartGrid vertical={false} />
            <ChartXAxis
              dataKey='spend'
              type='number'
              height={xAxisHeight}
              axisLine={xAxis.axisLine}
              tickFormatter={xAxis.tickFormatter}
              domain={xAxis.domain}
              ticks={xAxis.ticks}
              label={renderXAxisLabel(xAxisLabel)}
            />
            <ChartYAxis
              dataKey='response'
              width={yAxisWidth}
              tickFormatter={yAxis.tickFormatter}
              domain={yAxis.domain}
              ticks={yAxis.ticks}
            />
            {showTooltip && tooltip}

            {saturationData.newD.map(line => {
              const isActiveLine = activeLine === null ? true : activeLine === line.name;
              const isHoveredLegend = hoveredLegend === null ? true : hoveredLegend === line.name;
              const activeColor = isActiveLine ? colorMap[line.name] : Neutral500;
              const hoverColor = isHoveredLegend ? colorMap[line.name] : Neutral500;

              if (!isActiveLine && activeLine !== null) {
                return null;
              }
              return (
                <Line
                  data={line.data}
                  dataKey='response'
                  dot={
                    <SaturationCentralMeasureDot
                      saturationData={saturationData}
                      centralMeasure={centralMeasure}
                    />
                  }
                  isAnimationActive={false}
                  key={line.name}
                  name={line.name}
                  stroke={activeLine ? activeColor : hoverColor}
                  strokeWidth={3}
                />
              );
            })}
          </LC>
        )}
      />
    </div>
  </>
);
